"use client";

import { motion } from "framer-motion";
import { Building2, Users, GraduationCap } from "lucide-react";
const useCases = [{
  icon: Building2,
  title: "SMEs & Startups",
  description: "Perfect for small and medium enterprises looking to scale strategically. Get enterprise-level strategic planning tools without the enterprise complexity or cost.",
  benefits: ["Quick setup", "Affordable pricing", "Growth-focused templates"],
  color: "from-primary to-accent-green"
}, {
  icon: Users,
  title: "Business Consultants",
  description: "Enhance your consulting practice with AI-powered analysis tools. Deliver more value to clients with data-driven insights and professional presentations.",
  benefits: ["Client collaboration", "White-label reports", "Time-saving automation"],
  color: "from-accent-green to-accent-amber"
}, {
  icon: GraduationCap,
  title: "Students & Educators",
  description: "Learn strategic planning with real-world tools. Perfect for business schools, MBA programs, and students working on case studies and projects.",
  benefits: ["Educational discounts", "Learning resources", "Case study templates"],
  color: "from-accent-amber to-primary"
}];
export function UseCasesSection() {
  return <section id="use-cases" className="py-20 px-6 bg-gradient-to-br from-muted to-white" data-unique-id="c298caa9-a8b8-48fb-9373-656dc1561612" data-file-name="components/use-cases-section.tsx">
      <div className="max-w-7xl mx-auto" data-unique-id="bc27599a-6669-4db8-88fa-4b6084ba0fb3" data-file-name="components/use-cases-section.tsx">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} viewport={{
        once: true
      }} className="text-center mb-16" data-unique-id="11438173-8783-4be4-8984-6a005753f3b3" data-file-name="components/use-cases-section.tsx">
          <h2 className="text-4xl font-bold mb-4" data-unique-id="51b4ef10-842b-4968-8fec-af70c88b4272" data-file-name="components/use-cases-section.tsx"><span className="editable-text" data-unique-id="3a4ea8ed-8338-4041-9dd1-c8955a4da870" data-file-name="components/use-cases-section.tsx">
            Built for </span><span className="gradient-text" data-unique-id="586092b3-b194-4a04-8f4f-d009e1da144c" data-file-name="components/use-cases-section.tsx"><span className="editable-text" data-unique-id="c485fc44-ebe5-4fea-aa58-e05e54a3f2eb" data-file-name="components/use-cases-section.tsx">Every Strategic Need</span></span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto" data-unique-id="767aebe9-4949-4cf9-85bb-320264d3514e" data-file-name="components/use-cases-section.tsx"><span className="editable-text" data-unique-id="d5d593b5-0c76-46c0-aa4d-202410b9a190" data-file-name="components/use-cases-section.tsx">
            Whether you're scaling a startup, consulting for clients, or learning strategic planning, Acumena adapts to your needs
          </span></p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8" data-unique-id="f6705ce0-05da-411c-8846-3fdd34dc0442" data-file-name="components/use-cases-section.tsx" data-dynamic-text="true">
          {useCases.map((useCase, index) => <motion.div key={useCase.title} initial={{
          opacity: 0,
          y: 50
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: index * 0.2
        }} viewport={{
          once: true
        }} className="card-hover bg-white rounded-2xl p-8 border border-border shadow-lg" data-unique-id="f97ef822-ea7c-4f53-a64a-06c99229dcbb" data-file-name="components/use-cases-section.tsx">
              <div className="space-y-6" data-unique-id="2b5bcdb8-fbf2-42b1-9541-30db02eda247" data-file-name="components/use-cases-section.tsx">
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${useCase.color} flex items-center justify-center`} data-unique-id="02c3a855-39b7-4723-905c-86a4fb8c91a9" data-file-name="components/use-cases-section.tsx">
                  <useCase.icon className="w-8 h-8 text-white" data-unique-id="6782b8a8-ea07-4ca6-bcb9-91840445cfa0" data-file-name="components/use-cases-section.tsx" data-dynamic-text="true" />
                </div>
                
                <div className="space-y-4" data-unique-id="67bedabe-0d75-4233-9274-5ccf6c68e692" data-file-name="components/use-cases-section.tsx">
                  <h3 className="text-xl font-semibold" data-unique-id="923ba040-7640-4039-9ac3-798f85f1ec90" data-file-name="components/use-cases-section.tsx" data-dynamic-text="true">{useCase.title}</h3>
                  <p className="text-muted-foreground leading-relaxed" data-unique-id="bcb66d93-fd6b-48a7-bd5e-7e73e0ce702a" data-file-name="components/use-cases-section.tsx" data-dynamic-text="true">
                    {useCase.description}
                  </p>
                </div>

                <div className="space-y-3" data-unique-id="b5627906-da88-4e42-b275-818f75917015" data-file-name="components/use-cases-section.tsx">
                  <h4 className="font-semibold text-sm text-foreground" data-unique-id="31f8df7c-9aff-4e6c-88fb-cc2118bcd99e" data-file-name="components/use-cases-section.tsx"><span className="editable-text" data-unique-id="82d9bfea-c19b-4c17-9162-b13ddb259a27" data-file-name="components/use-cases-section.tsx">Key Benefits:</span></h4>
                  <ul className="space-y-2" data-unique-id="19d0e35b-9679-423a-916f-084e7b6e3c7a" data-file-name="components/use-cases-section.tsx" data-dynamic-text="true">
                    {useCase.benefits.map((benefit, idx) => <li key={idx} className="flex items-center gap-2 text-sm text-muted-foreground" data-unique-id="851d224c-b2f1-4895-a1c6-01b2dbddfffa" data-file-name="components/use-cases-section.tsx" data-dynamic-text="true">
                        <div className="w-1.5 h-1.5 bg-accent-green rounded-full" data-unique-id="caa9d7fa-a7ef-4e58-8df8-6f5d1266a127" data-file-name="components/use-cases-section.tsx"></div>
                        {benefit}
                      </li>)}
                  </ul>
                </div>

                <div className="pt-4" data-unique-id="d1e34099-ecb0-409d-a2bf-87bb327450c6" data-file-name="components/use-cases-section.tsx">
                  <button className="text-primary font-semibold hover:text-primary-dark transition-colors inline-flex items-center gap-2 group" data-unique-id="6b8f3f40-9f0a-42d2-8472-53560acaa037" data-file-name="components/use-cases-section.tsx"><span className="editable-text" data-unique-id="e7b9ba50-e9d9-455d-bed2-c2ebce9f827c" data-file-name="components/use-cases-section.tsx">
                    Get started
                    </span><motion.span className="inline-block" whileHover={{
                  x: 5
                }} transition={{
                  type: "spring",
                  stiffness: 400,
                  damping: 10
                }} data-unique-id="77074999-4f4c-4f69-aedb-15199d2fd7ef" data-file-name="components/use-cases-section.tsx"><span className="editable-text" data-unique-id="380f2fba-0186-49a1-9dd4-c71eeb5b60a6" data-file-name="components/use-cases-section.tsx">
                      →
                    </span></motion.span>
                  </button>
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}